import Pane from 'components/Common/Pane'
import { rem } from 'polished'
import type { ReactNode } from 'react'
import { useContext, useRef } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import TextButton from 'LuxKit/Button/TextButton'
import zIndex from 'styles/tools/z-index'
import WalledContent from 'components/Common/WalledContent'
import BodyText from 'LuxKit/Typography/BodyText'
import { useAppDispatch } from 'hooks/reduxHooks'
import { accountAccessShowLogin } from 'actions/UiActions'
import Group from 'components/utils/Group'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'
import GeoContext from 'contexts/geoContext'
import clsx from 'clsx'
import noop from 'lib/function/noop'
import OfferRating from 'components/Common/NumberRating/OfferRating'
import { useAppFooterObserver } from 'hooks/useAppLayoutObservers'

const FooterWrapper = styled(Pane)`
  bottom: 0;
  box-shadow: ${(props) => props.theme.shadow.top.small};
  display: flex;
  flex-direction: column;
  padding: ${rem(8)} ${rem(16)} ${rem(20)};
  position: fixed;
  width: 100vw;
  z-index: ${zIndex.stickyFooter};
  transition: transform 500ms ease-in-out;
  transform: translateY(calc(100%));

  ${mediaQueryUp.tablet} {
    display: none;
  }

  &.visible {
    transform: translateY(0);
  }
`

const ContentContainer = styled.div`
  display: flex;
  gap: ${rem(12)};
  justify-content: space-between;
  align-items: center;
`

const AuthWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;

  & > * + * {
    margin-inline-start: ${rem(16)};
  }
`

const StyledPackageOptionsButton = styled(TextButton)`
  margin-inline-start: auto;
`

interface Props {
  children: ReactNode
  callToActionDisabled?: boolean
  callToActionCopy?: string
  onCTAClick?: () => void
  hidePricing?: boolean
  enforcedWalledGarden?: boolean
  offer?: App.Offer | App.BedbankOffer
  visible?: boolean
  className?: string
}

function OfferPageStickyFooter(props: Props) {
  const {
    callToActionDisabled = false,
    callToActionCopy,
    onCTAClick = noop,
    hidePricing = false,
    enforcedWalledGarden,
    offer,
    children,
    visible,
    className,
  } = props
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const { currentRegionCode } = useContext(GeoContext)
  const footerRef = useRef<HTMLDivElement>(null)

  useAppFooterObserver(footerRef, visible ? 'sticky' : 'hidden')

  const isUSRegion = currentRegionCode === 'US'
  const hasReview = !isUSRegion && offer?.property?.rating && isOfferRatingDisplayable(offer.property.rating)

  const defaultCallToActionCopy = intl.formatMessage({
    id: 'q2guZPrnxE20XOF1UckP',
    defaultMessage: 'View options',
  })
  const finalCallToActionCopy = callToActionCopy ?? defaultCallToActionCopy
  const isWalledGardenEnforced = enforcedWalledGarden ?? !!offer?.walledGarden

  return (
    <FooterWrapper ref={footerRef} className={clsx({ visible }, className)}>
      <ContentContainer>
        <WalledContent
          enforced={isWalledGardenEnforced}
          hidePricing={hidePricing}
          wall={(
            <AuthWrapper>
              {offer && hasReview && offer.property?.rating && <>
                <Group direction="vertical" gap={4}>
                  <BodyText variant="medium" weight="bold">
                    {intl.formatMessage({
                      id: 'o0WUMwNI7SvV9qESG-aU',
                      defaultMessage: 'Log in to view member prices',
                    })}
                  </BodyText>
                  {isOfferRatingDisplayable(offer.property.rating) && <OfferRating
                    variant="xs"
                    rating={offer.property.rating}
                    inlineLabel
                  />}
                </Group>
                <TextButton kind="secondary" onClick={() => dispatch(accountAccessShowLogin())}>
                  {intl.formatMessage({
                    id: '_GGIGRrzIPZARiHblWqr',
                    defaultMessage: 'Log in or sign up',
                  })}
                </TextButton>
              </>}
            </AuthWrapper>
          )}
        >
          {children}
          {!callToActionDisabled && <StyledPackageOptionsButton kind="primary" onClick={onCTAClick}>
            {finalCallToActionCopy}
          </StyledPackageOptionsButton>}
        </WalledContent>
      </ContentContainer>
    </FooterWrapper>
  )
}

export default OfferPageStickyFooter
